if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Metre increments: the visible quantity input is in metres for the
        // per-metre variant of 'metre-increments' products (1 unit = 0.1m).
        // Convert metres to Shopify units and enforce the 1m minimum.
        const formIdAttr = this.form.getAttribute('id');
        const metreInput = formIdAttr
          ? document.querySelector(`input[name="quantity"][form="${formIdAttr}"][data-metre-mode]`)
          : this.form.querySelector('input[name="quantity"][data-metre-mode]');
        if (metreInput) {
          const metres = parseFloat(formData.get('quantity'));
          const cartUnits = parseInt(metreInput.dataset.cartQuantity || '0', 10) || 0;
          const minMetres = cartUnits >= 10 ? 0.1 : 1;
          if (isNaN(metres) || metres < minMetres - 0.001) {
            this.handleErrorMessage(`Please enter at least ${minMetres}m — fabric is sold in 0.1m increments with a 1m minimum.`);
            this.submitButton.setAttribute('aria-disabled', false);
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner').classList.add('hidden');
            return;
          }
          // Metre increments: one-tap confirmation before fabric goes in the basket.
          if (!this.metreConfirmed) {
            this.submitButton.setAttribute('aria-disabled', false);
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.showMetreConfirm(metres);
            return;
          }
          this.metreConfirmed = false;
          formData.set('quantity', String(Math.round(metres * 10)));
        }

        // Free trade samples: approved trade customers (tag `free-samples`) get a
        // fixed allowance of sample units per order, in any combination. Check the
        // basket before adding so we can say no politely rather than trimming later.
        const sampleLimit = (window.OttolineSampleCap || {}).limit || 0;
        const sampleVariantIds = (this.dataset.sampleVariantIds || '').split(',').filter(Boolean);
        if (sampleLimit > 0 && sampleVariantIds.includes(String(formData.get('id'))) && !this.sampleChecked) {
          const wanted = parseInt(formData.get('quantity') || '1', 10) || 1;
          const resume = () => {
            this.sampleChecked = true;
            // Dawn's own re-entry guard would swallow the resubmit, so clear it first.
            this.submitButton.setAttribute('aria-disabled', false);
            if (this.form.requestSubmit) this.form.requestSubmit();
            else this.form.dispatchEvent(new Event('submit', { cancelable: true }));
          };
          fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
            .then((response) => response.json())
            .then((cart) => {
              const used = (cart.items || []).reduce(
                (total, item) =>
                  /(^|[^a-z])samples?([^a-z]|$)/i.test(`${item.variant_title || ''} ${item.title || ''}`)
                    ? total + item.quantity
                    : total,
                0
              );
              const remaining = Math.max(0, sampleLimit - used);
              if (wanted > remaining) {
                this.handleErrorMessage(
                  remaining === 0
                    ? `Your trade account includes ${sampleLimit} free samples per order, and your basket already has ${sampleLimit}.`
                    : `Your trade account includes ${sampleLimit} free samples per order \u2014 you have ${remaining} left.`
                );
                this.submitButton.setAttribute('aria-disabled', false);
                this.submitButton.classList.remove('loading');
                this.querySelector('.loading__spinner').classList.add('hidden');
                return;
              }
              resume();
            })
            .catch(resume);
          return;
        }
        this.sampleChecked = false;

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }


      showMetreConfirm(metres) {

        const existing = document.getElementById('MetreConfirmDialog');

        if (existing) existing.remove();

        const metresDisplay = Math.round(metres * 10) / 10;

        const scope = this.closest('product-info') || document;

        const titleEl =

          scope.querySelector('.product__title h1, .product__title h2, h2.h1, h1') ||

          document.querySelector('h1');

        const title = titleEl ? titleEl.textContent.trim() : 'this fabric';

        let totalText = '';

        const massyTotal = scope.querySelector('.metre-massy-total');

        const retailTotal = scope.querySelector('[data-metre-total]');

        if (massyTotal && massyTotal.textContent.indexOf(':') > -1) {

          totalText = massyTotal.textContent.split(':').slice(1).join(':').trim();

        } else if (retailTotal && retailTotal.offsetParent !== null) {

          totalText = retailTotal.textContent.trim();

        }

        const overlay = document.createElement('div');

        overlay.id = 'MetreConfirmDialog';

        overlay.className = 'metre-confirm-overlay';

        const box = document.createElement('div');

        box.className = 'metre-confirm';

        box.setAttribute('role', 'dialog');

        box.setAttribute('aria-modal', 'true');

        const msg = document.createElement('p');

        msg.className = 'metre-confirm__message';

        msg.textContent = 'You\u2019ve selected ' + metresDisplay + ' metre' + (metresDisplay === 1 ? '' : 's') + ' of ' + title + (totalText ? ' \u2014 ' + totalText : '') + '. Happy to continue?';

        const actions = document.createElement('div');

        actions.className = 'metre-confirm__actions';

        const yes = document.createElement('button');

        yes.type = 'button';

        yes.className = 'button metre-confirm__yes';

        yes.textContent = 'Yes, add to basket';

        const no = document.createElement('button');

        no.type = 'button';

        no.className = 'button button--secondary metre-confirm__no';

        no.textContent = 'Go back';

        actions.appendChild(no);

        actions.appendChild(yes);

        box.appendChild(msg);

        box.appendChild(actions);

        overlay.appendChild(box);

        document.body.appendChild(overlay);

        const close = () => overlay.remove();

        no.addEventListener('click', close);

        overlay.addEventListener('click', (e) => {

          if (e.target === overlay) close();

        });

        yes.addEventListener('click', () => {

          close();

          this.metreConfirmed = true;

          if (this.form.requestSubmit) {

            this.form.requestSubmit();

          } else {

            this.onSubmitHandler(new Event('submit', { cancelable: true }));

          }

        });

        yes.focus();

      }


      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
